import axios from "axios";

export const API_BASE_URL =
    process.env.API_BASE_URL || "http://localhost:3001/api";

const TOKEN_STORAGE_KEY = 'cuntas_session_token';

/**
 * Thin wrapper around fetch that adds the ngrok-skip-browser-warning header
 * in development. Use this instead of raw fetch() for all API calls.
 */
export function apiFetch(url: string, init?: RequestInit): Promise<Response> {
    const headers = new Headers(init?.headers);
    if (process.env.NODE_ENV === "development") {
        headers.set("ngrok-skip-browser-warning", "true");
    }
    return fetch(url, { ...init, headers });
}

const httpClient = axios.create({
    baseURL: process.env.API_BASE_URL || 'http://localhost:3001/api',
    timeout: 10000,
    headers: process.env.NODE_ENV === "development" ? {
        "ngrok-skip-browser-warning": true
    } : undefined
});

// Add auth token to all requests if available
httpClient.interceptors.request.use((config) => {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

export { httpClient };

export const GET_SKILLS_AND_CONCEPTS = "getContentSubjectExtra"
export const GET_LEARNING_OUTCOMES_CONTENT_OBJECTIVES = "getContentObjectives"

interface StrandDataParams {
    classIds: number[];
    subjectIds: number[];
    strandIds: number[];
    strandUnitIds: number[];
    action: "getContentSub"
}

interface Class {
    classgroup_id: string
    description: string
    id: string
    modifiedOn: string // datetime in format "2024-06-12 10:20:30"
    name: string
}

interface Subject {
    extraTotal: string,
    id: string,
    name: string,
    sen_subject_id: string
}

interface ClassesResponse {
    result: Class[]
}

interface SubjectResponse {
    data: Subject[]
}

interface StrandResponse {
    data: any[],
    success: boolean
}

interface LearningOutcomeOrContentObjective {
    class_id: string,
    class_ids: string,
    content: string,
    displayOrder: string,
    extraTotal: string,
    id: string,
    sourceData: string,
    strand: string,
    strand_id: string,
    strandunit: string,
    strandunit_child_id: string,
    strandunit_id: string,
    subject: string,
    subject_id: string,
    trail: string
}

interface LearningOutcomeAndContentObjectiveResponse {
    data: LearningOutcomeOrContentObjective[]
}


interface SkillsAndConceptsData {
    class: string,
    displayOrder: string,
    id: string,
    parent_id: string,
    subjectExtra: string,
    subject_id: string,
    trail: string,
    category?: string,
    element?: string
}

interface GroupedSkillsAndConcepts {
    [category: string]: {
        [element: string]: SkillsAndConceptsData[]
    }
}

interface SkillsAndConceptsResponse {
    data: SkillsAndConceptsData[],
    grouped?: GroupedSkillsAndConcepts
}


export async function getClasses(): Promise<ClassesResponse> {
    const classes = await httpClient.get("/classes")
    return classes.data
}

export async function getSubjects(classIds: string[] = []): Promise<SubjectResponse> {
    const subjects = await httpClient.get("/subjects", {
        params: {
            classIds: classIds.join(',')
        }
    })
    return subjects.data
}

export async function getStrands(classIds: number[], subjectIds: number[]): Promise<StrandResponse> {
    const strands = await httpClient.get("/strands", {
        params: {
            classIds: classIds.join(','),
            subjectIds: subjectIds.join(',')
        }
    })
    return strands.data
}

export async function getStrandUnits(classIds: number[], subjectIds: number[], strandIds: number[]) {
    const strandUnits = await httpClient.get("/strand-units", {
        params: {
            classIds: classIds.join(','),
            subjectIds: subjectIds.join(','),
            strandIds: strandIds.join(',')
        }
    })
    if (strandUnits.status !== 200) {
        console.error("Error fetching strand units", strandUnits);
        return [];
    }
    return strandUnits.data
}

export async function getContentObjectives(classIds: number[], subjectIds: number[], strandIds: number[], strandUnitIds: number[]): Promise<LearningOutcomeAndContentObjectiveResponse> {
    const contentObjectives = await httpClient.get("/content-objectives", {
        params: {
            classIds: classIds.join(','),
            subjectIds: subjectIds.join(','),
            strandIds: strandIds.join(','),
            strandUnitIds: strandUnitIds.join(',')
        }
    })
    return contentObjectives.data
}

export async function getSkillsAndConcepts(classIds: number[], subjectIds: number[]): Promise<SkillsAndConceptsResponse> {
    const skillsAndConcepts = await httpClient.get<SkillsAndConceptsResponse>("/skills-and-concepts", {
        params: {
            classIds: classIds.join(','),
            subjectIds: subjectIds.join(',')
        }
    })

    const wrangled = skillsAndConcepts.data.data.map(item => {
        // Parse trail: "Third Class, Fourth Class > Working as an historian > Time and chronology"
        const trailParts = item.trail.split(' > ');
        const category = trailParts[1]?.trim() || '';
        const element = trailParts[2]?.trim() || '';

        return {
            ...item,
            subjectExtra: item.subjectExtra.split("\n").map(s => s[0].toUpperCase() + s.slice(1)).join(".\n") + ".",
            category,
            element
        }
    })

    // Group by category then element
    const grouped: GroupedSkillsAndConcepts = {};
    wrangled.forEach(item => {
        if (!item.category) return;

        if (!grouped[item.category]) {
            grouped[item.category] = {};
        }

        if (!item.element) return;

        if (!grouped[item.category][item.element]) {
            grouped[item.category][item.element] = [];
        }

        grouped[item.category][item.element].push(item);
    });

    return { data: wrangled, grouped }
}
